import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, assetUrl, errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Check, XCircle, CheckCheck, MessageSquare, LayoutTemplate } from "lucide-react";
import SafeZoneOverlay from "../components/SafeZoneOverlay";
import WatermarkOverlay from "../components/WatermarkOverlay";

const PLATFORM_LABEL = { instagram: "Instagram", linkedin: "LinkedIn", twitter: "X / Twitter", facebook: "Facebook", tiktok: "TikTok" };

function PortalCard({ post, token, onUpdate, showGuides }) {
  const [comment, setComment] = useState("");

  const act = async (action) => {
    try {
      const { data } = await api.post(`/approve/${token}/posts/${post.id}`, { action, comment: comment || null });
      onUpdate(data);
      setComment("");
      toast.success(action === "approve" ? "Post approved" : "Changes requested");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const wmProps = post.wm_url
    ? { wmUrl: assetUrl(post.wm_url), position: post.wm_position || "bottom-right", scale: post.wm_scale || 300, opacity: post.wm_opacity ?? 0.6 }
    : null;

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`portal-post-${post.id}`}>
      {post.video_url ? (
        <div className="relative bg-black">
          <video src={assetUrl(post.video_url)} poster={post.video_poster_url ? assetUrl(post.video_poster_url) : (post.video_image_url ? assetUrl(post.video_image_url) : (post.image_url ? assetUrl(post.image_url) : undefined))} controls playsInline preload="metadata" className="w-full aspect-[9/16] object-cover bg-black" data-testid={`portal-video-${post.id}`} />
          {showGuides && wmProps && <WatermarkOverlay {...wmProps} />}
          {showGuides && <SafeZoneOverlay wm={{ position: post.wm_position, scale: post.wm_scale }} />}
        </div>
      ) : post.image_url ? (
        <div className="relative">
          <img src={assetUrl(post.image_url)} alt="" className="w-full aspect-square object-cover" />
          {showGuides && wmProps && <WatermarkOverlay {...wmProps} />}
          {showGuides && <SafeZoneOverlay wm={{ position: post.wm_position, scale: post.wm_scale }} />}
        </div>
      ) : null}
      <div className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            <Badge variant="secondary">{PLATFORM_LABEL[post.platform] || post.platform}</Badge>
            {post.brand && <Badge className="bg-accent text-accent-foreground border-0">{post.brand}</Badge>}
          </div>
          <Badge className="capitalize" variant={post.review_status === "approved" ? "default" : post.review_status === "changes_requested" ? "destructive" : "outline"} data-testid={`portal-status-${post.id}`}>
            {post.review_status.replace("_", " ")}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">{new Date(post.scheduled_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
        <p className="text-sm whitespace-pre-wrap leading-relaxed">{post.copy}</p>
        <p className="text-xs text-accent">{(post.hashtags || []).map((h) => `#${h}`).join(" ")}</p>
        {post.comments?.length > 0 && (
          <div className="space-y-1.5">
            {post.comments.map((c, i) => (
              <p key={i} className="text-xs p-2 rounded bg-muted flex gap-1.5"><MessageSquare size={12} className="shrink-0 mt-0.5" /><span><span className="font-semibold">{c.author}:</span> {c.text}</span></p>
            ))}
          </div>
        )}
        <Input data-testid={`portal-comment-input-${post.id}`} placeholder="Leave a comment (optional)" value={comment} onChange={(e) => setComment(e.target.value)} className="text-sm" />
        <div className="flex gap-2">
          <Button size="sm" className="flex-1" data-testid={`portal-approve-btn-${post.id}`} onClick={() => act("approve")}><Check size={13} className="mr-1" /> Approve</Button>
          <Button size="sm" variant="outline" className="flex-1 text-destructive" data-testid={`portal-changes-btn-${post.id}`} onClick={() => act("request_changes")}><XCircle size={13} className="mr-1" /> Request changes</Button>
        </div>
      </div>
    </div>
  );
}

export default function ApprovalPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [showGuides, setShowGuides] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/approve/${token}`).then((r) => setData(r.data)).catch((e) => setError(errText(e)));
  }, [token]);

  const onUpdate = (p) => setData((d) => ({ ...d, posts: d.posts.map((x) => (x.id === p.id ? p : x)) }));

  const approveAll = async () => {
    await api.post(`/approve/${token}/approve-all`);
    const { data: fresh } = await api.get(`/approve/${token}`);
    setData(fresh);
    toast.success("All pending posts approved");
  };

  if (error) return <div className="min-h-screen flex items-center justify-center bg-background"><p className="text-destructive">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><img src="/logo.png" alt="" className="w-16 h-16 animate-pulse rounded-full" /></div>;

  const approved = data.posts.filter((p) => ["approved", "scheduled"].includes(p.review_status)).length;

  return (
    <div className="min-h-screen bg-background grain" data-testid="approval-portal-page">
      <header className="sticky top-0 z-20 bg-background/85 backdrop-blur-xl border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="" className="w-9 h-9 rounded-full" />
            <div>
              <p className="font-heading font-semibold leading-tight">{data.brand_name || "Harmony Wellness Group"}</p>
              <p className="text-xs text-muted-foreground">{data.batch.name} · client approval</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button type="button" data-testid="portal-safe-zone-toggle" onClick={() => setShowGuides((v) => !v)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${showGuides ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:text-foreground"}`}>
              <LayoutTemplate size={13} className="inline mr-1.5 -mt-0.5" /> Safe zones
            </button>
            <div className="w-44">
              <p className="text-xs font-semibold mb-1" data-testid="portal-progress-text">{approved} of {data.posts.length} approved</p>
              <Progress value={data.posts.length ? (approved / data.posts.length) * 100 : 0} className="h-2" />
            </div>
            <Button data-testid="portal-approve-all-btn" onClick={approveAll}><CheckCheck size={14} className="mr-2" /> Approve all</Button>
          </div>
        </div>
      </header>
      <main className="relative z-10 max-w-5xl mx-auto px-6 py-10">
        <h1 className="font-heading text-3xl font-semibold mb-2">Review your upcoming content</h1>
        <p className="text-muted-foreground mb-8">Approve each post or request changes — no account needed.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.posts.map((p) => <PortalCard key={p.id} post={p} token={token} onUpdate={onUpdate} showGuides={showGuides} />)}
        </div>
      </main>
    </div>
  );
}
